import { ReactElement, useState } from 'react';
import {
    BarChart,
    Bar,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    Cell,
    CartesianGrid,
    TooltipContentProps
} from 'recharts';
import { ChartContainer } from './chart-container';
import { blue, fontColorLight, moonlight, opacity, red } from '@/ui/styles';
import { UIText } from '@/ui';
import { useAnalyticsTranslation } from '../hooks';
import { useTasksAndAlertsAnalytics } from '../providers/tasks-and-alerts-analytics-provider';
import { CategoricalChartFunc } from 'recharts/types/chart/types';

const INACTIVE_BAR_OPACITY = 0.3;
const CORNER_RADIUS = 8;

function CustomTooltip(
    props: TooltipContentProps<number, string>
): ReactElement {
    const { active, payload } = props;
    if (!active || !payload || payload.length === 0) {
        return <></>;
    }

    type ChartData = {
        taskCount: number;
        alertCount: number;
        dayOfTheWeek: string;
    };
    const data = (payload[0] as { payload: ChartData }).payload;
    const taskCount = data.taskCount ?? 0;
    const alertCount = data.alertCount ?? 0;
    const dayOfTheWeek = data.dayOfTheWeek ?? '';

    return (
        <TooltipContent
            taskCount={taskCount}
            alertCount={alertCount}
            dayOfTheWeek={dayOfTheWeek}
        />
    );
}

function TooltipContent(props: {
    taskCount: number;
    alertCount: number;
    dayOfTheWeek: string;
}): ReactElement {
    const { taskCount, alertCount, dayOfTheWeek } = props;
    const { t } = useAnalyticsTranslation();

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'white',
                borderRadius: 8,
                border: `1px solid ${moonlight}`,
                gap: 8,
                padding: 8
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                }}
            >
                <div
                    style={{
                        width: 12,
                        height: 12,
                        borderRadius: 12,
                        backgroundColor: blue
                    }}
                />
                <UIText>
                    {t('shared.charts.tasks_value', { value: taskCount })}
                </UIText>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                    style={{
                        width: 12,
                        height: 12,
                        borderRadius: 12,
                        backgroundColor: red
                    }}
                />
                <UIText>
                    {t('shared.charts.alerts_value', { value: alertCount })}
                </UIText>
            </div>
            <UIText type="default2" variant="small">
                {dayOfTheWeek}
            </UIText>
        </div>
    );
}

export function DayOfTheWeekActivityBarChart(): ReactElement {
    const { t } = useAnalyticsTranslation();
    const { dayOfTheWeekActivityData, dateString } =
        useTasksAndAlertsAnalytics();

    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const handleMouseMove: CategoricalChartFunc = (state) => {
        const { isTooltipActive, activeTooltipIndex } = state;
        if (!isTooltipActive) {
            setActiveIndex(null);
        } else if (
            (typeof activeTooltipIndex === 'number' && activeTooltipIndex) ||
            activeTooltipIndex === 0
        ) {
            setActiveIndex(activeTooltipIndex);
        }
    };

    const handleMouseLeave = () => {
        setActiveIndex(null);
    };

    const getBarOpacity = (index: number): number => {
        if (activeIndex === null || index === activeIndex) {
            return 1;
        } else {
            return INACTIVE_BAR_OPACITY;
        }
    };

    return (
        <ChartContainer
            title={t('components.day_of_the_week_activity_bar_chart.title')}
            titleWeight="medium"
            subtitle={dateString || '...'}
            description={t(
                'components.day_of_the_week_activity_bar_chart.description'
            )}
        >
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={dayOfTheWeekActivityData}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                >
                    <CartesianGrid
                        horizontal={true}
                        vertical={false}
                        stroke={moonlight}
                    />

                    <XAxis
                        dataKey="dayOfTheWeek"
                        axisLine={{
                            stroke: moonlight
                        }}
                        tick={{
                            fontSize: 11,
                            fontFamily: `'Ubuntu', sans-serif`,
                            fill: fontColorLight
                        }}
                    />

                    <YAxis
                        width={20}
                        axisLine={false}
                        tickLine={false}
                        tick={{
                            fontSize: 11,
                            fontFamily: `'Ubuntu', sans-serif`,
                            fill: fontColorLight
                        }}
                    />

                    <Tooltip
                        content={CustomTooltip}
                        cursor={{
                            fill: moonlight + opacity(0.6)
                        }}
                    />

                    <Bar
                        key="taskCount"
                        dataKey="taskCount"
                        stackId="a"
                        fill={blue}
                        radius={[CORNER_RADIUS, CORNER_RADIUS, 0, 0]}
                    >
                        {dayOfTheWeekActivityData.map((entry, index) => {
                            const keys = Object.keys(entry);
                            const values = Object.values(entry);
                            const alertCountDataIndex = keys.findIndex(
                                (key) => key === 'alertCount'
                            );

                            // If the alertCount is 0, we need to round the corners
                            if (
                                alertCountDataIndex >= 0 &&
                                values[alertCountDataIndex] === 0
                            ) {
                                return (
                                    <Cell
                                        key={`cell-${index}`}
                                        opacity={getBarOpacity(index)}
                                    />
                                );
                            }
                            // Else do not round the corners
                            return (
                                <Cell
                                    key={`cell-${index}`}
                                    radius={0}
                                    opacity={getBarOpacity(index)}
                                />
                            );
                        })}
                    </Bar>
                    <Bar
                        key="alertCount"
                        dataKey="alertCount"
                        stackId="a"
                        fill={red}
                        radius={[CORNER_RADIUS, CORNER_RADIUS, 0, 0]}
                    >
                        {dayOfTheWeekActivityData.map((_, index) => {
                            return (
                                <Cell
                                    key={`cell-${index}`}
                                    opacity={getBarOpacity(index)}
                                />
                            );
                        })}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </ChartContainer>
    );
}
